import { useWindowWidth } from '@react-hook/window-size';
import { Trans, useTranslation } from 'react-i18next';
import { HiOutlineChevronDoubleLeft, HiOutlineChevronDoubleRight } from 'react-icons/hi';
import { useParams } from 'react-router-dom';
import { useRunOnce } from '../../components/RunOnce.tsx';
import { useAppDispatch, useAppSelector } from '../../lib/hooks.ts';
import { selectDate } from '../../lib/model/date/dateSlice.ts';
import DayOverview from './DayOverview.tsx';
import ProtectedRoute from '../../components/ProtectedRoute';
import { Button, Datepicker, ToggleSwitch, WeekStart } from 'flowbite-react';
import { DateTime } from 'luxon';
import { Suspense, useState } from 'react';

enum ViewType {
    DAY,
    WORKWEEK
}

interface View {
    dayMapper: (date: DateTime<boolean>) => Array<DateTime<boolean>>
    previous: (date: DateTime<boolean>) => DateTime<boolean>
    next: (date: DateTime<boolean>) => DateTime<boolean>
    title: () => string
}

const MIN_WEEK_WINDOW_SIZE = 1024

const VIEWS : Record<ViewType, View> = {
    [ViewType.DAY]: {
        dayMapper: (date) => {
            return [date]
        },
        previous: (date) => {
            do {
                date = date.minus({days: 1})
            } while (date.isWeekend)

            return date
        },
        next: (date) => {
            do {
                date = date.plus({days: 1})
            } while(date.isWeekend)

            return date
        },
        title: () => { return "day"}
    },
    [ViewType.WORKWEEK]: {
        dayMapper: (date: DateTime<boolean>) => {
            let startOfWeek = date.startOf('week')
            if (date.isWeekend) {
                // On weekends, show the next week
                startOfWeek = startOfWeek.plus({week: 1})
            }

            return Array.from({length: 5}, (_, k) => k)
                .map(i => startOfWeek.plus({days: i}))
        },
        previous: (date) => {
            return date.minus({week: 1})
        },
        next: (date) => {
            return date.plus({week: 1})
        },
        title: () => { return "week" }
    },
}

export default function Overview() {
    const dispatch = useAppDispatch()
    const screenWith = useWindowWidth()
    const params = useParams();
    const date = DateTime.fromISO(useAppSelector(state => state.date.date))
    const [showEmpty, setShowEmpty] = useState<boolean>(false)
    const {t} = useTranslation("overview");

    const { i18n } = useTranslation("overview");

    const view = screenWith < MIN_WEEK_WINDOW_SIZE ? ViewType.DAY : ViewType.WORKWEEK

    useRunOnce(() => {
        if (params.date !== undefined) {
            dispatch(selectDate(DateTime.fromISO(params.date)))
        }
    });

    function dateChanged(date: Date | null) {
        if (date) {
            dispatch(selectDate(DateTime.fromJSDate(date)))
        }
    }

    function previous() {
        dispatch(selectDate(VIEWS[view].previous(date)))
    }

    function next() {
        dispatch(selectDate(VIEWS[view].next(date)))
    }

    const days = VIEWS[view].dayMapper(date)

    return (
        <div>
            <ProtectedRoute authenticated={true}>
                <div className={"flex flex-row pb-1 items-end"}>
                    <Button onClick={previous} color="clear">
                        <HiOutlineChevronDoubleLeft/>
                    </Button>
                    <div className={"flex flex-col w-full"}>
                        <Trans t={t}>{VIEWS[view].title()}</Trans>
                        <Datepicker onChange={dateChanged}
                                    language={i18n.language}
                                    className={"w-full"}
                                    weekStart={WeekStart.Monday}
                                    value={date.toJSDate()}/>
                    </div>
                    <Button onClick={next} color="clear">
                        <HiOutlineChevronDoubleRight/>
                    </Button>
                </div>
                <div className={"flex flex-row w-full"}>
                    {days.map((day, index) =>
                        (
                            <Suspense key={index}>
                                <DayOverview day={day} showEmpty={showEmpty}/>
                            </Suspense>
                        )
                    )}
                </div>
                <ToggleSwitch onChange={setShowEmpty} checked={showEmpty} label={t("show_empty")} className={"mt-2"}/>
            </ProtectedRoute>
        </div>
    )
}
